import { Campaign, EmailProvider } from '@/client/campaigns';
import { Dialog } from 'primereact/dialog';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { WhitelistingModal } from './WhitelistingModal';

const StyledDialog = styled(Dialog)`
  width: 700px;

  .p-dialog-title {
    font-size: var(--heading-small-font-size) !important;
    font-weight: 600 !important;
    color: var(--black-main) !important;
  }

  .p-dialog-header {
    padding: var(--default-padding) var(--default-padding) 0
      var(--default-padding) !important;
  }

  .p-dialog-content {
    padding: 0 var(--default-padding) var(--default-padding)
      var(--default-padding) !important;
  }
`;

const ModalDescription = styled.p`
  color: var(--black-main);
  font-size: var(--small-font-size);
  line-height: 1.5;
  margin: 0 0 var(--semi-big-padding) 0;
`;

const ProviderOptionsContainer = styled.div`
  display: flex;
  gap: var(--medium-padding);
  margin-bottom: var(--xsmall-padding);
`;

const ProviderOption = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--semi-big-padding);
  padding-right: var(--default-padding);
  border: 1px solid var(--gray-light);
  border-radius: var(--small-border-radius);
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 90px;

  .arrow-icon {
    transition: all 0.4s ease;
  }

  &:hover {
    .arrow-icon {
      color: var(--green-color);
      background-color: var(--green-opacity);
    }
  }
`;

const ProviderName = styled.span`
  color: var(--black-main);
  font-size: var(--heading-small-font-size);
  font-weight: 600;
`;

const ArrowIcon = styled.span`
  color: var(--black-main);
  font-size: var(--semi-big-font-size);
  font-weight: bold;
  padding: var(--xsmall-padding);
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--xsmall-border-radius);
  transition: all 0.2s ease;
`;

type EmailProviderModalProps = {
  isOpen: boolean;
  campaign: Campaign;
  onClose: () => void;
};

export const EmailProviderModal: React.FC<EmailProviderModalProps> = ({
  isOpen,
  campaign,
  onClose,
}) => {
  const { t } = useTranslation();
  const [isWhitelistingModalOpen, setIsWhitelistingModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] =
    useState<EmailProvider | null>(null);

  const handleProviderSelect = (provider: EmailProvider) => {
    setSelectedProvider(provider);
    setIsWhitelistingModalOpen(true);
    onClose();
  };

  const handleCloseWhitelistingModal = () => {
    setIsWhitelistingModalOpen(false);
    setSelectedProvider(null);
  };

  const handleCloseEmailProviderModal = () => {
    onClose();
    setSelectedProvider(null);
  };

  return (
    <>
      <StyledDialog
        visible={isOpen}
        onHide={handleCloseEmailProviderModal}
        header={t('phishing.simulation.emailProvider.title')}
        modal
        draggable={false}
        resizable={false}
        className="v2"
        closable={true}
        closeOnEscape={true}
      >
        <div>
          <ModalDescription>
            {t('phishing.simulation.emailProvider.description')}
          </ModalDescription>

          <ProviderOptionsContainer>
            <ProviderOption
              onClick={() => handleProviderSelect(EmailProvider.MICROSOFT)}
            >
              <ProviderName>
                {t('phishing.simulation.emailProvider.microsoft')}
              </ProviderName>
              <ArrowIcon className="arrow-icon">
                <i className="pi pi-arrow-right" />
              </ArrowIcon>
            </ProviderOption>

            <ProviderOption
              onClick={() => handleProviderSelect(EmailProvider.GOOGLE)}
            >
              <ProviderName>
                {t('phishing.simulation.emailProvider.google')}
              </ProviderName>
              <ArrowIcon className="arrow-icon">
                <i className="pi pi-arrow-right" />
              </ArrowIcon>
            </ProviderOption>
          </ProviderOptionsContainer>
        </div>
      </StyledDialog>

      {selectedProvider && (
        <WhitelistingModal
          isOpen={isWhitelistingModalOpen}
          onClose={handleCloseWhitelistingModal}
          provider={selectedProvider}
          accountId={campaign.account.id}
        />
      )}
    </>
  );
};
